import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { setAuthToken, APP_ORIGIN } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowRight, Mail, Lock, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { RelevoLogo } from "../components/RelevoLogo";
import { t } from "../lib/i18n";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo ha fallado. Inténtalo de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" · ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!loading && user) {
      if (!user.role) navigate("/role-select");
      else if (user.role === "seller") navigate("/seller/dashboard");
      else if (user.role === "searcher") navigate("/searcher/marketplace");
    }
  }, [user, loading, navigate]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogle = () => {
    const redirectUrl = APP_ORIGIN + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.email || !form.password || (mode === "register" && !form.name)) {
      setError("Completa todos los campos.");
      return;
    }
    setSubmitting(true);
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const body =
        mode === "login"
          ? { email: form.email, password: form.password }
          : { name: form.name, email: form.email, password: form.password };
      const r = await api.post(endpoint, body);
      if (r.data.token) setAuthToken(r.data.token);
      const u = r.data.user;
      setUser(u);
      toast.success(mode === "login" ? "Bienvenido de nuevo" : "Cuenta creada");
      if (!u.role) navigate("/role-select");
      else if (u.role === "seller") navigate("/seller/dashboard");
      else if (u.role === "investor") navigate("/investor/marketplace");
      else navigate("/searcher/marketplace");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const switchMode = (m) => {
    setMode(m);
    setError("");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hairline-b lg:hairline-b-0 flex items-center justify-center p-8 lg:p-16">
        <div className="max-w-md w-full space-y-8 fade-up">
          <a href="/" className="flex items-center gap-3" data-testid="login-logo">
            <RelevoLogo className="text-3xl" />
          </a>

          <div className="space-y-3">
            <span className="font-mono-label text-gold">
              {mode === "login" ? t("Accede a tu cuenta") : t("Crea tu cuenta")}
            </span>
            <h1 className="font-serif-display text-4xl md:text-5xl leading-tight">
              {mode === "login" ? t("Bienvenido de nuevo.") : t("Únete a Relevo.")}
            </h1>
            <p className="text-muted-foreground text-sm">
              {mode === "login"
                ? t("Identifícate con tu email, Google o Meta para entrar a tu panel.")
                : t("Empieza tu camino. Tu rol (Vendedor o Searcher) se confirma en el siguiente paso.")}
            </p>
          </div>

          {/* SOCIAL */}
          <div className="space-y-3">
            <Button
              type="button"
              onClick={handleGoogle}
              variant="outline"
              className="rounded-none w-full border-[#1E3A5F] text-[#1E3A5F] hover:bg-secondary h-11"
              data-testid="login-google-btn"
            >
              <GoogleIcon />{" "}{t("Continuar con Google")}
            </Button>
            <div className="relative">
              <Button
                type="button"
                disabled
                variant="outline"
                className="rounded-none w-full h-11 border-border text-muted-foreground opacity-60 cursor-not-allowed"
                data-testid="login-meta-btn"
              >
                <MetaIcon />{" "}{t("Continuar con Meta")}
              </Button>
              <span className="absolute -top-2 right-3 font-mono-label bg-bone hairline px-2 py-0.5">
                {t("Próximamente")}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-border" />
            <span className="font-mono-label text-muted-foreground">{t("o con email")}</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* EMAIL/PASSWORD FORM */}
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="email-auth-form">
            {mode === "register" && (
              <Field icon={<UserIcon className="w-4 h-4" />} label={t("Nombre completo")}>
                <Input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={t("María García")}
                  className="rounded-none pl-10"
                  data-testid="login-name-input"
                  autoComplete="name"
                />
              </Field>
            )}
            <Field icon={<Mail className="w-4 h-4" />} label={t("Email")}>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder={t("tu@email.com")}
                className="rounded-none pl-10"
                data-testid="login-email-input"
                autoComplete="email"
              />
            </Field>
            <Field icon={<Lock className="w-4 h-4" />} label={t("Contraseña")}>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={mode === "register" ? t("Mínimo 8 caracteres, letras y números") : "••••••••"}
                className="rounded-none pl-10"
                data-testid="login-password-input"
                autoComplete={mode === "register" ? "new-password" : "current-password"}
              />
            </Field>

            {mode === "login" && (
              <div className="flex justify-end -mt-1">
                <Link
                  to="/forgot-password"
                  className="font-mono-label text-muted-foreground hover:text-gold"
                  data-testid="forgot-password-link"
                >
                  {t("¿Olvidaste tu contraseña?")}
                </Link>
              </div>
            )}

            {error && (
              <div className="hairline bg-destructive/5 text-destructive px-3 py-2 text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="rounded-none w-full bg-[#1E3A5F] hover:bg-[#2B4C78] text-bone h-12"
              data-testid="login-submit-btn"
            >
              {submitting ? t("Procesando…") : mode === "login" ? t("Iniciar sesión") : t("Crear cuenta")}{" "}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </form>

          <div className="text-sm text-center">
            {mode === "login" ? (
              <>
                {t("¿No tienes cuenta?")}{" "}
                <button
                  type="button"
                  onClick={() => switchMode("register")}
                  className="text-gold font-medium underline-offset-2 hover:underline"
                  data-testid="switch-to-register"
                >
                  {t("Regístrate")}
                </button>
              </>
            ) : (
              <>
                {t("¿Ya tienes cuenta?")}{" "}
                <button
                  type="button"
                  onClick={() => switchMode("login")}
                  className="text-gold font-medium underline-offset-2 hover:underline"
                  data-testid="switch-to-login"
                >
                  {t("Inicia sesión")}
                </button>
              </>
            )}
          </div>

          <p className="text-xs text-muted-foreground text-center leading-relaxed max-w-sm mx-auto" data-testid="login-confidentiality-note">
            {t("Al registrarte, te unes a una red privada de inversión donde la confidencialidad es la norma. Los acuerdos específicos se gestionarán individualmente para cada oportunidad.")}
          </p>
        </div>
      </div>

      <div className="hidden lg:block relative overflow-hidden" data-testid="login-side-image">
        <img
          src="/images/login-office.jpg"
          alt={t("Oficina corporativa minimalista")}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#1E3A5F]/60"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A5F]/90 via-transparent to-transparent"></div>
        <div className="absolute top-0 left-0 right-0 h-1 bg-brand-orange"></div>
        <div className="relative h-full flex flex-col justify-end p-12 text-bone">
          <span className="font-mono-label opacity-70">{t("Marketplace · Search Funds")}</span>
          <p className="font-serif-display text-3xl mt-3 max-w-md leading-tight">
            {t("\"Una sola plataforma para conectar oportunidades, capital y talento del search fund hispano.\"")}
          </p>
        </div>
      </div>
    </div>
  );
}

const Field = ({ icon, label, children }) => (
  <div className="space-y-1.5">
    <Label className="font-mono-label text-muted-foreground">{label}</Label>
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">{icon}</span>
      {children}
    </div>
  </div>
);

const GoogleIcon = () => (
  <svg className="w-4 h-4 mr-2" viewBox="0 0 48 48">
    <path fill="#EA4335" d={t("M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z")}/>
    <path fill="#4285F4" d={t("M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z")}/>
    <path fill="#FBBC05" d={t("M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z")}/>
    <path fill="#34A853" d={t("M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z")}/>
  </svg>
);

const MetaIcon = () => (
  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="#1877F2">
    <path d={t("M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z")} />
  </svg>
);
